import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Link } from 'react-router'
import { getFormValues } from 'redux-form'
import * as flashActions from 'redux-flash'
import { ContentBlock, FormBlock } from 'components'
import {
  ApplicationFormStep1,
  ApplicationFormStep2,
  ApplicationFormStep3,
} from '../forms'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors as partnerPortalSelectors } from 'partner-portal-reducer'
import { selectors as partnerProfileSelectors } from '../../profile/reducer'
import * as apiActions from 'api-actions'
import * as Types from 'types'
import { buildPartnerApplicationPayload } from 'utils'
import { SIGN_IN_ROUTE } from 'config'

const APPLICATION_STEPS = [
  { step: 1, progressLabel: 'General Info (Page 1/3)' },
  { step: 2, progressLabel: 'General Program Information (Page 2/3)' },
  { step: 3, progressLabel: 'Program Details (Page 3/3)' },
]

const STEP1_FORM = 'partner-application-step1'
const STEP2_FORM = 'teaching-artist-application-step2'

const TARGET_ANSWERABLE_TYPES = new Set([
  'school',
  'grade',
  'ca_common_core',
  'organization',
])

function buildIdNameMap(items, labelKey) {
  const map = {}
  ;(items || []).forEach((item) => {
    if (!item || item.id == null) return
    const label = item[labelKey]
    if (label == null || label === '') return
    map[String(item.id)] = String(label)
  })
  return map
}

function buildAnswerableTypeByVariable(questions) {
  const map = {}
  ;(questions || []).forEach((question) => {
    const variable = question?.variable
    const answerableType = question?.answerableType || question?.answerable_type
    if (!variable || !TARGET_ANSWERABLE_TYPES.has(answerableType)) return
    map[variable] = answerableType
  })
  return map
}

function formatSubmitErrors(err) {
  const raw = err?.errors?._error
  if (Array.isArray(raw)) return raw.join(' ')
  if (typeof raw === 'string') return raw
  return 'Something went wrong. Please try again.'
}

function ApplicationSubmittedSuccess() {
  return (
    <div className="application-submitted">
      <div className="application-submitted-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="36" height="36" role="img">
          <path
            fill="currentColor"
            d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
          />
        </svg>
      </div>
      <h1 className="application-submitted-title">Application Submitted!</h1>
      <p className="application-submitted-body">
        Thank you for sharing your information. Your profile has been received
        and is now pending review. <br />
        You'll receive an email once your submission has been approved and
        published. <br />
        <br />
        In the meantime, look out for an email to finish setting up your
        account.
      </p>
      <Link
        to={SIGN_IN_ROUTE}
        className="button-primary application-submitted-login"
      >
        Back to Log In
      </Link>
    </div>
  )
}

const propTypes = {
  disciplines: PropTypes.arrayOf(Types.discipline),
  applicationQuestions: PropTypes.arrayOf(Types.applicationQuestion),
  leadershipCharacteristics: PropTypes.array,
  grades: PropTypes.array,
  schoolOptions: PropTypes.array,
  organizationOptions: PropTypes.array,
  caCommonCores: PropTypes.array,
  step1Values: PropTypes.object,
  step2Values: PropTypes.object,
  fetchApplicationQuestions: PropTypes.func.isRequired,
  fetchCaCommonCores: PropTypes.func.isRequired,
  fetchSchoolOptions: PropTypes.func.isRequired,
  fetchOrganizationOptions: PropTypes.func.isRequired,
  fetchGrades: PropTypes.func.isRequired,
  fetchLeadershipCharacteristics: PropTypes.func.isRequired,
  submitPartnerApplication: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
}

function Application({
  disciplines,
  applicationQuestions,
  leadershipCharacteristics,
  grades,
  schoolOptions,
  organizationOptions,
  caCommonCores,
  step1Values,
  step2Values,
  fetchApplicationQuestions,
  fetchCaCommonCores,
  fetchSchoolOptions,
  fetchOrganizationOptions,
  fetchGrades,
  fetchLeadershipCharacteristics,
  submitPartnerApplication,
  flashErrorMessage,
}) {
  const [currentStep, setCurrentStep] = useState(1)
  const [applicationPrograms, setApplicationPrograms] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [applicationSubmitting, setApplicationSubmitting] = useState(false)

  useEffect(() => {
    fetchApplicationQuestions('Organization')
    fetchCaCommonCores()
    fetchSchoolOptions()
    fetchOrganizationOptions()
    fetchGrades()
    fetchLeadershipCharacteristics()
  }, [])

  const handleFinalSubmit = useCallback(
    (programs) => {
      if (!programs || programs.length === 0) return
      const step2SerializationConfig = {
        answerableTypeByVariable:
          buildAnswerableTypeByVariable(applicationQuestions),
        idToLabelByAnswerableType: {
          school: buildIdNameMap(schoolOptions, 'name'),
          organization: buildIdNameMap(organizationOptions, 'name'),
          grade: buildIdNameMap(grades, 'displayName'),
          ca_common_core: buildIdNameMap(caCommonCores, 'name'),
        },
      }

      const payload = buildPartnerApplicationPayload(
        step1Values,
        step2Values,
        programs,
        step2SerializationConfig
      )
      setApplicationSubmitting(true)
      return submitPartnerApplication(payload).then(
        () => {
          setSubmitted(true)
          setApplicationSubmitting(false)
        },
        (err) => {
          flashErrorMessage(formatSubmitErrors(err))
          setApplicationSubmitting(false)
        }
      )
    },
    [
      step1Values,
      step2Values,
      applicationQuestions,
      schoolOptions,
      organizationOptions,
      grades,
      caCommonCores,
      submitPartnerApplication,
      flashErrorMessage,
    ]
  )

  const stepPanelClass = (step) =>
    [
      'application-step-panel',
      currentStep !== step ? 'application-step-panel--hidden' : '',
    ]
      .filter(Boolean)
      .join(' ')

  if (submitted) {
    return (
      <ContentBlock>
        <FormBlock>
          <div className="form-block-inner">
            <ApplicationSubmittedSuccess />
          </div>
        </FormBlock>
      </ContentBlock>
    )
  }

  return (
    <ContentBlock>
      <FormBlock>
        <div className="form-block-inner">
          <div className="application-header-row">
            {currentStep > 1 && (
              <button
                type="button"
                className="application-header-back"
                aria-label="Back"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="23"
                  height="10"
                  viewBox="0 0 23 10"
                  fill="none"
                >
                  <path
                    d="M4.38367 9.024C3.72234 8.256 3.02901 7.57333 2.30367 6.976C1.57834 6.37867 0.810339 5.87733 -0.000328064 5.472V3.584C1.64234 2.73067 3.10367 1.536 4.38367 9.53674e-07H7.71167C7.54101 0.533334 7.30634 1.07733 7.00767 1.632C6.73034 2.18667 6.39967 2.74133 6.01567 3.296V5.76C6.39967 6.29333 6.73034 6.83733 7.00767 7.392C7.28501 7.92533 7.51967 8.46933 7.71167 9.024H4.38367ZM5.82367 6.24L5.85567 2.784H22.5277V6.24H5.82367Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            )}
            <h1>Organization Application</h1>
          </div>
          <p>
            Complete the fields below to submit an application for your
            Organization account in artlook. Once you click Submit, your
            responses will be finalized and you won’t be able to return to make
            changes. Please review carefully before submitting.
          </p>
          <div className="application-progress-header" role="progressbar">
            {APPLICATION_STEPS[currentStep - 1].progressLabel}
          </div>
          <div className="application-right-block">
            <div className="application-step-content">
              <div className={stepPanelClass(1)}>
                <ApplicationFormStep1
                  disciplines={disciplines}
                  leadershipCharacteristics={leadershipCharacteristics}
                  onSubmit={() => setCurrentStep(2)}
                />
              </div>
              <div className={stepPanelClass(2)}>
                <ApplicationFormStep2
                  questions={applicationQuestions}
                  grades={grades}
                  schoolOptions={schoolOptions}
                  organizationOptions={organizationOptions}
                  caCommonCores={caCommonCores}
                  onSubmit={() => setCurrentStep(3)}
                />
              </div>
              <div className={stepPanelClass(3)}>
                <ApplicationFormStep3
                  programs={applicationPrograms}
                  onProgramsChange={setApplicationPrograms}
                  onSubmitApplication={handleFinalSubmit}
                  applicationSubmitting={applicationSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </FormBlock>
    </ContentBlock>
  )
}

Application.propTypes = propTypes

function mapStateToProps(state) {
  return {
    disciplines: globalSelectors.disciplines(state),
    applicationQuestions: globalSelectors.applicationQuestions(state),
    leadershipCharacteristics:
      partnerProfileSelectors.leadershipCharacteristics(state),
    grades: globalSelectors.grades(state),
    schoolOptions: partnerPortalSelectors.schoolOptions(state),
    caCommonCores: globalSelectors.caCommonCores(state),
    organizationOptions: globalSelectors.organizationOptions(state),
    step1Values: getFormValues(STEP1_FORM)(state),
    step2Values: getFormValues(STEP2_FORM)(state),
  }
}

const mapDispatchToProps = {
  fetchApplicationQuestions: apiActions.fetchApplicationQuestions,
  fetchCaCommonCores: apiActions.fetchCaCommonCores,
  fetchSchoolOptions: apiActions.fetchSchoolOptions,
  fetchOrganizationOptions: apiActions.fetchOrganizationOptions,
  fetchGrades: apiActions.fetchGrades,
  fetchLeadershipCharacteristics: apiActions.fetchLeadershipCharacteristics,
  submitPartnerApplication: apiActions.submitPartnerApplication,
  flashErrorMessage: flashActions.flashErrorMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  Application
)
